import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Button, ErrorAlert, Field, InfoField } from "src/components";
import DropdownAnimation from "src/components/UI/dropdownAnimation/DropdownAnimation";
import { useDeposit } from "src/hooks";
import { formatNumberToLocale } from "src/utils";
import AlfredPaymentInstructions from "./AlfredPaymentInstructions";
import "./deposit-form.scss";

const DepositForm: React.FC = (): JSX.Element => {
  const { t } = useTranslation();
  const {
    amount,
    currency,
    quote,
    quoteLoading,
    stableAmount,
    paymentFee,
    paymentFeeLoading,
    handleAmountChange,
    handleInitiateDeposit,
    initiateDepositLoading,
    initiateDepositError,
    setAmountTouched,
    errors,
    submitDisabled,
    amountTouched,
    options,
    selectedOption,
    selectedCurrency,
    setSelectedCurrency,
    optionsLoading,
    paymentInstructions,
    notAvailableForCountry,
    sessionId,
    resetDeposit,
    bankId,
    setBankId,
    requiresBankId,
    bankIdValid,
  } = useDeposit();

  const { currency_id, precision } = currency;

  // Sum of Alfred's fee breakdown (commission/processing/tax/network).
  // Alfred deducts these from the user's fiat payment before converting
  // to stable; we display the total in the source-fiat for clarity.
  const alfredFeeTotal = useMemo(() => {
    if (!quote) return 0;
    return quote.fees.reduce((sum, f) => sum + Number(f.amount), 0);
  }, [quote]);

  // Net stable the user actually receives in their wallet:
  //   stableAmount (Alfred conversion) − platform fee (in stable)
  // Platform fee comes from `v4Service.estimatePaymentFee` in the
  // wallet currency, so subtraction is unit-correct.
  const netReceiveStable = Math.max(stableAmount - Number(paymentFee || 0), 0);

  // Map Alfred options into the shape the shared DropdownAnimation
  // expects: `key` is the value (currency code) and `value` is the
  // display label "MXN — SPEI".
  const dropdownOptions = useMemo(
    () =>
      options.map((opt) => ({
        key: opt.source_currency,
        value: `${opt.source_currency} — ${opt.rail}`,
      })),
    [options]
  );

  // Inline min/max hint shown as the amount-field placeholder.
  const limitsHint = selectedOption
    ? t("DEPOSIT_FORM.LIMITS_HINT", {
        min: selectedOption.min_amount,
        max: selectedOption.max_amount,
        currency: selectedOption.source_currency,
      })
    : undefined;

  // Limit errors are the actionable ones — show them first.
  const firstError =
    errors.alfredLimitErrors[0] ??
    errors.inputErrors[0] ??
    errors.paymentValidationErrors[0] ??
    errors.paymentFeeErrors[0];

  // Early returns must come AFTER all hook calls (rules-of-hooks).
  if (paymentInstructions && sessionId) {
    return (
      <AlfredPaymentInstructions
        sessionId={sessionId}
        transaction={paymentInstructions}
        onReset={resetDeposit}
      />
    );
  }

  if (notAvailableForCountry) {
    return (
      <section className="deposit-form deposit-form--unavailable">
        <div className="deposit-form__unavailable-icon" aria-hidden>
          ✕
        </div>
        <h2 className="deposit-form__unavailable-title">
          {t("DEPOSIT_FORM.METHOD_NOT_AVAILABLE_TITLE")}
        </h2>
        <p className="deposit-form__unavailable-text">
          {t("DEPOSIT_FORM.METHOD_NOT_AVAILABLE_TEXT")}
        </p>
      </section>
    );
  }

  return (
    <form className="deposit-form" onSubmit={handleInitiateDeposit}>
      <DropdownAnimation
        label={t("DEPOSIT_FORM.PAY_WITH")}
        selectedValue={selectedCurrency}
        options={dropdownOptions}
        placeholder={t("DEPOSIT_FORM.SELECT_CURRENCY")}
        onChange={(field) => setSelectedCurrency(field.key)}
        disabled={optionsLoading || initiateDepositLoading}
      />

      <Field
        label={
          selectedOption
            ? t("DEPOSIT_FORM.AMOUNT_VIA_RAIL", {
                rail: selectedOption.rail,
                currency: selectedOption.source_currency,
              })
            : t("DEPOSIT_FORM.AMOUNT")
        }
        value={amount}
        onChange={(value) => handleAmountChange(value)}
        placeholder={limitsHint ?? t("DEPOSIT_FORM.AMOUNT_PLACEHOLDER_ZERO")}
        disabled={initiateDepositLoading || !selectedOption}
        error={amountTouched ? firstError : undefined}
        onBlur={() => setAmountTouched(true)}
      />

      {/* Country-specific gate: today only Colombia (COP / PSE) needs
          a bank pre-selected upfront — Alfred 422s without
          `metadata.bankId`. We surface a free-text input rather than
          a hardcoded list so onboarding new banks doesn't ship a
          widget update; format validation mirrors the BE DTO
          (`/^\d{4}$/`). */}
      {requiresBankId && (
        <Field
          label="PSE bank code"
          value={bankId}
          onChange={(value) => setBankId(value.replace(/\D/g, "").slice(0, 4))}
          placeholder="4-digit code (e.g. 1007)"
          disabled={initiateDepositLoading}
          error={bankId && !bankIdValid ? "Must be 4 digits" : undefined}
        />
      )}

      <InfoField
        className="info-field--muted"
        label={
          selectedOption
            ? t("DEPOSIT_FORM.ALFRED_FEES_VIA", {
                rail: selectedOption.rail,
              })
            : t("DEPOSIT_FORM.ALFRED_FEES")
        }
        value={
          quote
            ? `${formatNumberToLocale(alfredFeeTotal, 2)} ${quote.fromCurrency}`
            : "—"
        }
        isLoading={quoteLoading}
      />

      <InfoField
        className="info-field--muted"
        label={t("DEPOSIT_FORM.PLATFORM_FEE")}
        value={`${formatNumberToLocale(paymentFee, precision)} ${currency_id}`}
        isLoading={paymentFeeLoading}
      />

      <InfoField
        label={t("DEPOSIT_FORM.RECEIVE_TOTAL")}
        value={`${formatNumberToLocale(netReceiveStable, precision)} ${currency_id}`}
        isLoading={paymentFeeLoading || quoteLoading}
      />

      <ErrorAlert message={initiateDepositError} />

      <Button
        title={t("DEPOSIT_FORM.NEXT_STEP")}
        className="deposit-button"
        type="submit"
        disabled={submitDisabled}
        pending={initiateDepositLoading}
        tooltipMessage={Object.values(errors).flat()[0]}
      />
    </form>
  );
};

export default DepositForm;
